import { FormlyFieldConfig } from '@ngx-formly/core';
import { formInput, formInputPlace, formRow, formRepeatSection, formSelectCountry } from '@rocketfinancialcorp/rocket-ui/form';
import { Patterns, toTitleCase } from '@shared/utils';
import { AddressType } from '@shared/models';

export const addressFormFields = ({
  type,
  onMakePrimaryClick,
  onSectionRemove,
  isRequired,
}: {
  type: AddressType;
  onSectionRemove: (i: number, remove: (index: number) => void) => void;
  onMakePrimaryClick: (i: number) => void;
  isRequired?: boolean;
}) => {
  const expressions = {
    'props.required': (field: FormlyFieldConfig) => {
      if (isRequired && field.model?.primary) {
        return true;
      }
      return (
        !!field.model?.addressLine1 ||
        !!field.model?.addressLine2 ||
        !!field.model?.city ||
        !!field.model?.state ||
        !!field.model?.postalCode ||
        !!field.model?.country
      );
    },
  };

  const addressTypeLabel = toTitleCase(type);
  return [
    formRepeatSection({
      key: 'addresses',
      unstyled: true,
      addText: `Add ${addressTypeLabel} Address`,
      removeText: 'Remove',
      header: `${addressTypeLabel} Address`,
      subheader: type === 'PHYSICAL' ? 'Must not be a PO box' : undefined,
      extraButtons: [
        {
          label: 'Set as Primary Address',
          type: 'radio',
          skipFirstSection: true,
          onClick: onMakePrimaryClick,
        },
      ],
      onRemove: onSectionRemove,
      fieldGroup: [
        {
          key: 'type',
          defaultValue: type,
        },
        formRow(
          [
            formInput({
              key: 'firstName',
              label: 'First Name',
              props: {
                pattern: Patterns.GENERIC_NAME,
                minLength: 1,
                maxLength: 40,
              },
              validation: { messages: { pattern: 'Enter a valid first name' } },
            }),
            formInput({
              key: 'middleName',
              label: 'Middle Name',
              props: {
                pattern: Patterns.GENERIC_NAME,
                minLength: 1,
                maxLength: 40,
              },
              validation: { messages: { pattern: 'Enter a valid middle name' } },
            }),
            formInput({
              key: 'lastName',
              label: 'Last Name',
              props: {
                pattern: Patterns.GENERIC_NAME,
                minLength: 1,
                maxLength: 40,
              },
              validation: { messages: { pattern: 'Enter a valid last name' } },
            }),
          ],
          { isHidden: type !== 'SHIPPING' },
        ),
        formRow([
          formInputPlace({
            key: 'addressLine1',
            label: 'Address 1',
            props: {
              countryRestrictions: ['US', 'CA', 'AZ'],
            },
            expressions,
          }),
        ]),
        formRow([
          formInput({
            key: 'addressLine2',
            label: 'Address 2',
          }),
        ]),
        formRow([
          formInput({
            key: 'city',
            label: 'City',
            className: 'city-field',
            props: { minLength: 1, maxLength: 25 },
            expressions,
          }),
          formInput({
            key: 'state',
            label: 'State',
            className: 'state-field',
            props: { minLength: 2, maxLength: 3 },
            validators: { validation: [{ name: 'isValidStateCode' }] },
            expressions,
          }),
          formInput({
            key: 'postalCode',
            label: 'Zip Code',
            className: 'postal-code-field',
            props: { minLength: 1, maxLength: 10 },
            expressions,
          }),
        ]),
        formRow([
          formSelectCountry({
            key: 'country',
            label: 'Country',
            props: { placeholder: 'Select', countryRestrictions: ['US', 'CA', 'AZ'] },
            expressions,
          }),
        ]),
      ],
    }),
  ];
};
